import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import UPDATE_AVATAR from '../../../../graphql/mutation/user/updateAvatar';
import { ID } from 'types/typeDef';
import { LoadingContainer } from 'components/molecules/Loaders/LoadingContainer';
import { ErrorContainer } from 'components/organisms/Error/ErrorContainer';
import {
  AllAvatarsQuery,
  GetMyNoteInClasseQuery,
  OnUserAvatarMutation,
  OnUserAvatarMutationVariables
} from '../../../../graphql/generated/graphql';
import QUERY_AVATARS from '../../../../graphql/query/avatar/queryAvatars';
import FrontError from '../../../../helpers/errors/front.error';
import { AvatarChoiceBlockView } from './AvatarChoiceBlock.view';
import {
  getAvatarListFromQuery,
  getAvatarUrlFromUserQuery
} from '../../../../helpers/transformApiResponse/avatar';
import { getNoteDefFromQuery } from 'helpers/transformApiResponse/note';
import { useDispatch, useSelector } from 'react-redux';
import { updateAvatar } from '../../../../store/actions/user.actions';
import { QUERY_GET_MY_NOTE_IN_CLASSE } from 'graphql/query/note/getNoteQuery';
import { MY_ID } from 'helpers/user/authorization';
import { useRefetchData } from 'helpers/refetch/refetchData';
import toast from 'react-hot-toast';
import { getUserSidebar } from '../../../../store/selectors/user/user.selectors';
import { QUERY_USER_HOME } from '../../../../graphql/query/user/queryUserHome';
import { QUERY_USER_ACCOUNT } from '../../../../graphql/query/user/queryUserAccount';

interface AvatarChoiceBlockProps {
  userid: ID;
  visibleTitle?: boolean;
}

export const AvatarChoiceBlock = ({
  userid,
  visibleTitle
}: AvatarChoiceBlockProps) => {
  const note = useQuery<GetMyNoteInClasseQuery>(QUERY_GET_MY_NOTE_IN_CLASSE, {
    variables: { id: MY_ID }
  });
  useRefetchData(note.refetch);
  const { data, loading, error } = useQuery<AllAvatarsQuery>(QUERY_AVATARS);
  const user = useSelector(getUserSidebar);
  const dispatch = useDispatch();
  const [currentAvatar, setAvatar] = useState(null);
  const [updateUserAvatar] = useMutation<
    OnUserAvatarMutation,
    OnUserAvatarMutationVariables
  >(UPDATE_AVATAR, {
    refetchQueries: [
      { query: QUERY_USER_HOME, variables: { id: userid } },
      { query: QUERY_USER_ACCOUNT, variables: { id: userid } }
    ],
    onCompleted: res => {
      dispatch(
        updateAvatar(getAvatarUrlFromUserQuery(res.updateUsersPermissionsUser))
      );
      toast.success('Votre avatar a été mis à jour');
    },
    onError: () => {
      toast.error("Erreur dans la mise à jour de l'avatar");
    }
  });

  const QteVente =
    !note.loading &&
    getNoteDefFromQuery(note?.data.getMyNoteInClasse.note).qtevente;
  const overallAverage =
    !note.loading &&
    getNoteDefFromQuery(note?.data.getMyNoteInClasse.note).moyennegenerale;
  const allowDisplayBronze =
    (QteVente && QteVente >= 5) || user.role === 'professeur';

  const allowDisplaySilver =
    (QteVente && QteVente >= 15) || user.role === 'professeur';

  const allowDisplayGold =
    (QteVente && QteVente >= 50) || user.role === 'professeur';

  const allowDisplayHalloween =
    overallAverage >= 10 || user.role === 'professeur';

  if (loading || note.loading)
    return <LoadingContainer variant="AvatarChoiceBlock" />;
  if (error || note.error)
    return (
      <ErrorContainer
        marginTop={true}
        text="Erreur au chargement des avatars"
        detailErr={
          new FrontError('Erreur au chargement des avatars', {
            ...error
          })
        }
        needCaptureError={false}
      />
    );

  return (
    <AvatarChoiceBlockView
      avatars={getAvatarListFromQuery(data.avatars.data)}
      userid={userid}
      updateUserAvatar={updateUserAvatar}
      currentAvatar={currentAvatar}
      setAvatar={setAvatar}
      displayBronze={allowDisplayBronze}
      displaySilver={allowDisplaySilver}
      displayGold={allowDisplayGold}
      displayHalloween={allowDisplayHalloween}
      visibleTitle={visibleTitle}
    />
  );
};
